/**
 * A sales item's minimum selling price guards what the customer actually pays per unit, so it is
 * measured after the line discount. Checking the unit price alone leaves a hole: a price that
 * clears the floor can still be discounted below it.
 *
 * Mirrors the server-side MinimumSellingPriceRule — the backend is the authority, this exists so
 * the cashier gets feedback before submitting.
 */

/** Half a cent: amounts are held to 2 dp, so ignore float noise at that scale. */
export const PRICE_EPSILON = 0.005;

export interface DiscountedLine {
  quantity: number;
  unitPrice: number;
  discountType: 'fixed' | 'percent';
  discountValue: number;
}

/** The discount in currency terms, clamped to the line's own value. */
export const calcLineDiscountAmount = (line: DiscountedLine): number => {
  const base = line.quantity * line.unitPrice;
  if (line.discountType === 'percent') {
    return base * (Math.min(Math.max(line.discountValue, 0), 100) / 100);
  }
  return Math.min(Math.max(line.discountValue, 0), base);
};

/** What one unit nets after the line discount is spread across the line. */
export const calcNetUnitPrice = (line: DiscountedLine): number => {
  if (line.quantity <= 0) return line.unitPrice;
  return (line.quantity * line.unitPrice - calcLineDiscountAmount(line)) / line.quantity;
};

/**
 * True when the discounted per-unit price falls below `floor`.
 * A null/undefined or non-positive floor means the item has no minimum configured.
 */
export const isBelowMinimumPrice = (
  line: DiscountedLine,
  floor: number | null | undefined,
): boolean => {
  if (floor == null || floor <= 0 || line.quantity <= 0) return false;
  return calcNetUnitPrice(line) < floor - PRICE_EPSILON;
};
